import { MessageService } from 'primeng/api';
import { BrandStore } from '@/app/features/brand';
import { TranslateService } from '@ngx-translate/core';
import { LayoutsEnum } from '@/app/shared/enums/layouts.enum';
import { LanguageEnum } from '@/app/shared/enums/language.enum';
import { SettingsConst } from '@/app/shared/consts/settings.const';
import { of, tap, filter, switchMap, takeUntil, Observable } from 'rxjs';

import { DOCUMENT } from '@angular/common';
import { Title } from '@angular/platform-browser';
import { Data, Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { OnInit, Inject, Component, Renderer2, AfterViewInit } from '@angular/core';

import { DestroyService } from '@/app/core/services/destroy.service';
import { LocalStorageService } from '@/app/shared/services/local-storage.service';
import { TapfiliateService } from '@/app/core/services/tapfiliate/tapfiliate-service.service';

import { AlertComponent } from '@/app/shared/components/ui/alert/alert.component';
import { AuthLayoutComponent } from '@/app/layouts/auth-layout/auth-layout.component';
import { AdminLayoutComponent } from '@/app/layouts/admin-layout/admin-layout.component';
import { PreviewLayoutComponent } from './layouts/preview-layout/preview-layout.component';
import { DefaultLayoutComponent } from '@/app/layouts/default-layout/default-layout.component';
import { AdminDefaultLayoutComponent } from '@/app/layouts/admin-default-layout/admin-default-layout.component';

import { getFirstChild } from '@/app/shared/helpers/router.helper';

@Component({
  selector: 'app-root',
  providers: [MessageService],
  imports: [
    DefaultLayoutComponent,
    AuthLayoutComponent,
    AlertComponent,
    AdminLayoutComponent,
    PreviewLayoutComponent,
    AdminDefaultLayoutComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, AfterViewInit {
  protected readonly LayoutsEnum = LayoutsEnum;

  protected layout: LayoutsEnum = LayoutsEnum.Default;

  constructor(
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly destroyService: DestroyService,
    private readonly translate: TranslateService,
    private readonly localStorageService: LocalStorageService,
    private readonly brandStore: BrandStore,
    private readonly tapfiliateService: TapfiliateService,
    private titleService: Title,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.translate.addLangs([LanguageEnum.En]);

    this.translate.use(this.localStorageService.getItem(SettingsConst['languageStorageKey']) ?? LanguageEnum.En);

    this.activatedRoute = activatedRoute;
  }

  ngOnInit(): void {
    this.processRoute(this.activatedRoute).subscribe();

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        switchMap(() => this.processRoute(this.activatedRoute)),
        takeUntil(this.destroyService),
      )
      .subscribe();

    this.brandStore.loadBrand().subscribe((brandResponse) => {
      const hasCustomFavicon = !!brandResponse.payload.content.favicon_url;
      if (hasCustomFavicon) {
        this.changeFavicon(brandResponse.payload.content.favicon_url as string);
      }

      const hasCustomTitle = !!brandResponse.payload.content.title;
      if (hasCustomTitle) {
        this.changeTitle(brandResponse.payload.content.title as string);
      }
    });
  }

  ngAfterViewInit(): void {
    const queryParams = new URLSearchParams(location.search);
    const referralId = queryParams.get('ref');

    if (referralId) {
      localStorage.setItem('referralId', referralId);
    }
    this.tapfiliateService
      .trackEvent$('create', { integration: 'stripe' })
      .pipe(
        tap(() => console.info('Tapfiliate initialized with Stripe integration')),
        switchMap(() => {
          if (referralId) {
            return this.tapfiliateService.trackEvent$('track', 'click_event', {
              custom: { referralId },
            });
          }

          return of(null);
        }),
        switchMap(() => this.tapfiliateService.trackEvent$('detect')),
      )
      .subscribe();
  }

  private processRoute(route: ActivatedRoute) {
    const childRoute = getFirstChild(route);

    if (childRoute) {
      return (childRoute.data as Observable<Data | null>).pipe(
        tap((data) => {
          this.layout = data?.['layout'];
        }),
      );
    }

    return of(null);
  }

  private changeTitle(title: string) {
    this.titleService.setTitle(title);
  }

  private changeFavicon(url: string) {
    let link: HTMLLinkElement | null = this.document.querySelector("link[rel*='icon']");
    if (link) {
      this.renderer.setAttribute(link, 'href', url);
    } else {
      link = this.renderer.createElement('link');
      this.renderer.setAttribute(link, 'rel', 'icon');
      this.renderer.setAttribute(link, 'href', url);
      this.renderer.appendChild(this.document.head, link);
    }
  }
}
